<template>
  <main class="admin-shell admin-login-page">
    <div class="admin-login-page__language"><AdminLanguageSwitcher /></div>
    <section class="admin-login-page__card">
      <p class="admin-login-page__eyebrow">{{ t('admin.brand') }}</p>
      <h1>{{ t('admin.auth.title') }}</h1>
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        :validate-on-rule-change="false"
        label-position="top"
        @submit.prevent="submit"
      >
        <el-form-item :label="t('admin.auth.username')" prop="username">
          <el-input v-model="form.username" autocomplete="username" />
        </el-form-item>
        <el-form-item :label="t('admin.auth.password')" prop="password">
          <el-input
            v-model="form.password"
            type="password"
            autocomplete="current-password"
            show-password
            @keyup.enter="submit"
          />
        </el-form-item>
        <el-form-item v-if="tenantCodeMode" :label="t('admin.auth.tenant')" prop="tenantCode">
          <el-select
            v-model="form.tenantCode"
            class="admin-login-page__tenant"
            filterable
            allow-create
            default-first-option
            :loading="tenantLoading"
            :placeholder="t('admin.auth.tenantPlaceholder')"
          >
            <el-option
              v-for="tenant in tenantOptions"
              :key="String(tenant.tenantCode)"
              :label="tenant.tenantName"
              :value="String(tenant.tenantCode)"
            />
          </el-select>
        </el-form-item>
        <p v-if="errorMessage" class="admin-login-page__error" role="alert">
          {{ errorMessage }}
        </p>
        <el-button
          class="admin-login-page__submit"
          type="primary"
          native-type="submit"
          :loading="submitting"
        >
          {{ t('admin.auth.login') }}
        </el-button>
      </el-form>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { FormInstance, FormRules } from 'element-plus'
import { useI18n } from 'vue-i18n'

import { useAdminSessionStore } from '@/stores/admin/session'
import { tenantApi, type TenantListItem } from '@/api/admin/tenant'
import { isAdminTenantCodeMode } from '@/apps/admin/config/tenant-mode'
import { readAdminTenantCode } from '@/apps/admin/utils/tenant-preference'
import AdminLanguageSwitcher from '@/apps/admin/components/AdminLanguageSwitcher.vue'

const route = useRoute()
const router = useRouter()
const adminSession = useAdminSessionStore()
const { t } = useI18n()
const formRef = ref<FormInstance>()
const submitting = ref(false)
const errorMessage = ref('')
const tenantLoading = ref(false)
const tenantOptions = ref<TenantListItem[]>([])
const tenantCodeMode = isAdminTenantCodeMode()
const form = reactive({ username: '', password: '', tenantCode: readAdminTenantCode() })
const rules = computed<FormRules<typeof form>>(() => ({
  username: [{ required: true, message: t('admin.auth.usernameRequired'), trigger: 'blur' }],
  password: [{ required: true, message: t('admin.auth.passwordRequired'), trigger: 'blur' }],
  tenantCode: [
    { required: true, message: t('admin.auth.tenantRequired'), trigger: 'blur' },
    { pattern: /^[1-9]\d*$/, message: t('admin.auth.tenantInvalid'), trigger: 'blur' }
  ]
}))

onMounted(async () => {
  if (!tenantCodeMode) return

  tenantLoading.value = true
  try {
    tenantOptions.value = await tenantApi.list()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t('admin.auth.tenantLoadFailed')
  } finally {
    tenantLoading.value = false
  }
})

watchEffect(() => {
  if (typeof document !== 'undefined') {
    document.title = t('admin.brand')
  }
})

const getRedirect = (): string => {
  const redirect = route.query.redirect
  return typeof redirect === 'string' &&
    redirect.startsWith('/admin') &&
    !redirect.startsWith('/admin/login')
    ? redirect
    : '/admin/dashboard'
}

const submit = async (): Promise<void> => {
  if (!formRef.value || submitting.value) {
    return
  }

  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) {
    return
  }

  submitting.value = true
  errorMessage.value = ''
  try {
    await adminSession.login(form)
    await router.replace(getRedirect())
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t('admin.auth.failed')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.admin-login-page {
  position: relative;
  display: grid;
  min-height: 100vh;
  place-items: center;
}

.admin-login-page__language {
  position: absolute;
  top: 18px;
  right: 24px;
  color: var(--admin-color-text);
}

.admin-login-page__card {
  width: min(420px, calc(100% - 48px));
  padding: 40px;
  border: 1px solid var(--admin-color-border);
  border-radius: 12px;
  background: var(--admin-color-surface);
  box-shadow: 0 16px 40px rgb(15 23 42 / 8%);
}

.admin-login-page__card h1 {
  margin: 8px 0 16px;
  font-size: 28px;
}

.admin-login-page__card p {
  margin: 0;
  line-height: 1.6;
}

.admin-login-page__eyebrow {
  color: var(--admin-color-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.admin-login-page__error {
  margin: 0 0 16px !important;
  color: var(--admin-color-danger);
  font-size: 14px;
}

.admin-login-page__submit {
  width: 100%;
}

.admin-login-page__tenant {
  width: 100%;
}
</style>
